import React, { useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Warning, CheckCircle, Clock, Camera } from '@phosphor-icons/react';
import { Loader2, X } from 'lucide-react';

export function SecurityFormSheet({
  open, onOpenChange, editingObs,
  formData, setFormData, chantiers,
  submitting, onSubmit,
}) {
  const fileInputRef = useRef(null);
  const [selectedPhotos, setSelectedPhotos] = useState([]);

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setSelectedPhotos(prev => [...prev, ...files]);
  };

  const removePhoto = (index) => {
    setSelectedPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmitWithPhotos = (e) => {
    onSubmit(e, selectedPhotos);
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) setSelectedPhotos([]); onOpenChange(v); }}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" aria-describedby="obs-form-desc">
        <SheetHeader>
          <SheetTitle>{editingObs ? "Modifiér l'observation" : 'Observation sécurité'}</SheetTitle>
          <p id="obs-form-desc" className="text-sm text-[#4B5563]">
            {editingObs ? 'Modifiéz les informations' : 'Signaler une observation CSPS'}
          </p>
        </SheetHeader>
        <form onSubmit={handleSubmitWithPhotos} className="space-y-4 mt-6">
          <div>
            <Label>Chantier *</Label>
            <Select value={formData.chantier_id}
              onValueChange={(value) => setFormData({...formData, chantier_id: value})}
              disabled={!!editingObs}>
              <SelectTrigger className="mt-1 h-14" data-testid="obs-chantier-select">
                <SelectValue placeholder="Sélectionner un chantier" />
              </SelectTrigger>
              <SelectContent position="popper" className="z-[100]">
                {(chantiers || []).map(c => c && (
                  <SelectItem key={c.id} value={c.id} className="py-3">{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="title">Titre *</Label>
            <Input id="title" value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              placeholder="Ex: Port du casque non respecte" required
              data-testid="obs-title-input" className="mt-1 h-14" />
          </div>

          <div>
            <Label>Niveau de gravite</Label>
            <Select value={formData.severity}
              onValueChange={(value) => setFormData({...formData, severity: value})}>
              <SelectTrigger className="mt-1 h-14" data-testid="obs-severity-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent position="popper" className="z-[100]">
                <SelectItem value="high" className="py-3">
                  <div className="flex items-center gap-2"><Warning size={16} className="text-red-500" />Critique</div>
                </SelectItem>
                <SelectItem value="medium" className="py-3">
                  <div className="flex items-center gap-2"><Clock size={16} className="text-amber-500" />Modere</div>
                </SelectItem>
                <SelectItem value="low" className="py-3">
                  <div className="flex items-center gap-2"><CheckCircle size={16} className="text-emerald-500" />Faible</div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="description">Description *</Label>
            <Textarea id="description" value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Decrivez l'observation sécurité..." rows={4} required
              data-testid="obs-description-input" className="mt-1" />
          </div>

          {!editingObs && (
            <div>
              <Label>Photos</Label>
              <div className="mt-1 space-y-2">
                <input ref={fileInputRef} type="file" accept="image/*" multiple
                  onChange={handlePhotoSelect} className="hidden" />
                <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}
                  className="w-full h-14" data-testid="obs-photo-upload-btn">
                  <Camera size={20} className="mr-2" /> Ajouter des photos
                </Button>
                {selectedPhotos.length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {selectedPhotos.map((photo, index) => (
                      <div key={`${photo.name}-${index}`} className="relative aspect-square rounded-md overflow-hidden bg-gray-100">
                        <img src={URL.createObjectURL(photo)} alt={`Photo ${index + 1}`}
                          className="w-full h-full object-cover" />
                        <button type="button" onClick={() => removePhoto(index)}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full">
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1 h-14">
              Annuler
            </Button>
            <Button type="submit" disabled={submitting || !formData.chantier_id}
              className="flex-1 h-14 bg-[#FF6B00] hover:bg-orange-600" data-testid="obs-submit-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (editingObs ? 'Enregistrér' : 'Créer')}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
